import { Fragment, useEffect, useState } from "react";

export function Tag({ label, bg = "var(--line-soft)", color = "var(--muted)", xs }) {
  return (
    <span
      className="badge"
      style={{ background: bg, color, fontSize: xs ? 10 : undefined, padding: xs ? "1px 7px" : undefined }}
    >
      {label}
    </span>
  );
}

export function Dot({ color = "var(--primary)", size = 8 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%",
      background: color, flexShrink: 0, display: "inline-block",
    }} />
  );
}

export function Avatar({ name = "?", size = 36, bg, color }) {
  return (
    <div
      className="avatar"
      style={{ width: size, height: size, background: bg, color, fontSize: Math.round(size * 0.36) }}
    >
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

export function Spinner({ size = 20 }) {
  return (
    <div style={{
      width: size, height: size,
      border: "2px solid var(--line)",
      borderTopColor: "var(--primary)",
      borderRadius: "50%",
      animation: "spin .7s linear infinite",
    }} />
  );
}

export function Btn({ children, onClick, variant = "primary", sm, style = {}, disabled, full }) {
  const clsMap = {
    primary:   "btn-primary",
    secondary: "btn-secondary",
    accent:    "btn-primary",
    ghost:     "btn-ghost",
    danger:    "btn-danger",
    success:   "btn-subtle",
    indigo:    "btn-subtle",
    gold:      "btn-primary",
  };

  const overrides = {
    success: { background: "var(--success-soft)", color: "var(--success-ink)", border: "1px solid var(--success-soft)" },
    indigo:  { background: "var(--primary-soft)", color: "var(--primary)", border: "1px solid var(--primary-soft)" },
    // Kit 2026: dourado nunca é botão; "gold" passou a ser o botão principal navy.
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={[
        "btn",
        clsMap[variant] || "btn-subtle",
        sm ? "btn-sm" : "",
        full ? "btn-block" : "",
      ].filter(Boolean).join(" ")}
      style={{ ...(overrides[variant] || {}), ...style }}
    >
      {children}
    </button>
  );
}

export function Modal({ title, subtitle, onClose, children, width = 540 }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
        backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
        justifyContent: "center", zIndex: 200, padding: 16,
        animation: "ds-fade-in var(--d) var(--ease)",
      }}
    >
      <div
        className="card"
        style={{
          width: "100%", maxWidth: width, maxHeight: "90vh", overflowY: "auto", boxShadow: "var(--shadow-lg)",
          animation: "ds-scale-in var(--d-slow) var(--ease-spring)",
        }}
      >
        <div style={{
          padding: "18px 22px 14px", borderBottom: "1px solid var(--line-soft)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          position: "sticky", top: 0, background: "var(--surface)", zIndex: 1,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: "var(--faint)", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="btn btn-icon"
            style={{ width: 28, height: 28, flexShrink: 0 }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: "18px 22px" }}>{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children, half, third }) {
  const flex = half ? "0 0 calc(50% - 6px)" : third ? "0 0 calc(33.33% - 6px)" : "1 1 100%";
  return (
    <div className="field" style={{ flex }}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

export function Inp({ style = {}, className = "", ...p }) {
  return (
    <input {...p} className={`input ${className}`} style={style} />
  );
}

export function Sel({ children, style = {}, className = "", ...p }) {
  return (
    <div className="select-wrap">
      <select {...p} className={`select ${className}`} style={style}>
        {children}
      </select>
      <span style={{
        position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", color: "var(--faint)", fontSize: 10,
      }}>▾</span>
    </div>
  );
}

export function Txta({ style = {}, rows = 3, className = "", ...p }) {
  return (
    <textarea {...p} rows={rows} className={`textarea ${className}`} style={style} />
  );
}

export function ProgressBar({ done, total, color }) {
  if (!total) return null;
  const pct = Math.round((done / total) * 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <div className="progress" style={{ flex: 1 }}>
        <i style={{
          display: "block", height: "100%", borderRadius: "var(--r-pill)",
          background: pct === 100 ? "var(--success)" : color || "var(--primary)",
          width: `${pct}%`, transition: "width .3s",
        }} />
      </div>
      <span style={{ fontSize: 10, color: "var(--faint)", minWidth: 28, textAlign: "right" }}>{done}/{total}</span>
    </div>
  );
}

export function Empty({ icon = "◻", text = "Nenhum item ainda" }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--faint)" }}>
      <div style={{ fontSize: 26, marginBottom: 8, opacity: .35 }}>{icon}</div>
      <div style={{ fontSize: "var(--t-sm)" }}>{text}</div>
    </div>
  );
}

export function TabBar({ items, active, onChange, label = "Seções" }) {
  // Kit 2026: abas em pílula (navy = aba ativa).
  return (
    <div className="pill-tabs" role="tablist" aria-label={label} style={{ paddingBottom: 12, borderBottom: "1px solid var(--line)" }}>
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          className={`pill-tab${active === t.id ? " on" : ""}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function Card({ children, style = {}, onClick, hover }) {
  return (
    <div
      onClick={onClick}
      className={`card${hover || onClick ? " card-hover" : ""}`}
      style={style}
    >
      {children}
    </div>
  );
}

export function StatCard({ label, value, sub, color, icon }) {
  return (
    <div className="kpi" style={{ flex: 1 }}>
      <div className="kpi-top">
        <span className="kpi-label">{label}</span>
        {icon && (
          <div className="kpi-ic" style={color ? { color } : undefined}>
            <span style={{ fontSize: 14 }}>{icon}</span>
          </div>
        )}
      </div>
      <div className="kpi-val">{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export function FormRow({ children }) {
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
      {children}
    </div>
  );
}

/* ============================================================
   Kit visual RC2 2026
   ============================================================ */

export const MARCA_ESCURA = "/marca/rc2clicks-2026-escuro.png"; // para fundo claro
export const MARCA_CLARA = "/marca/rc2clicks-2026-claro.png";   // para fundo escuro

/** Logomarca 2026. fundo="escuro" usa a versão clara (menu, palco); sem isso, segue o tema. */
export function Marca({ fundo, height = 26, style = {} }) {
  if (fundo === "escuro") return <img src={MARCA_CLARA} alt="RC2Clicks" style={{ height, width: "auto", display: "block", ...style }} />;
  if (fundo === "claro") return <img src={MARCA_ESCURA} alt="RC2Clicks" style={{ height, width: "auto", display: "block", ...style }} />;
  return (
    <span style={{ display: "inline-block", ...style }}>
      <img className="marca marca-escuro" src={MARCA_ESCURA} alt="RC2Clicks" style={{ height }} />
      <img className="marca marca-claro" src={MARCA_CLARA} alt="" aria-hidden="true" style={{ height }} />
    </span>
  );
}

/**
 * Palco: faixa escura do topo da tela.
 * path="agenda" vira "rc2@crm: ~/agenda"; title + hl = título com uma palavra dourada.
 */
export function Stage({ path, title, hl, hlFirst, lead, actions, live, tabs, children, hasToday, produto = "crm" }) {
  const titulo = hlFirst && hl
    ? <><span className="hl">{hl}</span>{title ? " " : ""}{title}</>
    : <>{title}{hl ? " " : ""}{hl && <span className="hl">{hl}</span>}</>;
  return (
    <section className={`stage${hasToday ? " has-today" : ""}`}>
      <div className="stage-path">
        <i style={{ background: "#FF5F57" }} /><i style={{ background: "#FEBC2E" }} /><i style={{ background: "#28C840" }} />
        <span>rc2@{produto}: ~/<b>{path}</b></span>
      </div>
      <div className="stage-row">
        <div className="stage-title">
          <h1>{titulo}</h1>
          {lead && <div className="stage-lead">{lead}</div>}
        </div>
        {(actions || live) && (
          <div className="stage-actions">
            {live && <span className="stage-live">● {live}</span>}
            {actions}
          </div>
        )}
      </div>
      {tabs}
      {children}
    </section>
  );
}

/** Abas em pílula para usar dentro do palco: items = [{id, label}] */
export function StageTabs({ items, active, onChange, label = "Seções" }) {
  return (
    <div className="pill-tabs" role="tablist" aria-label={label}>
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={active === t.id}
          className={`pill-tab${active === t.id ? " on" : ""}`} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** "O que fazer hoje": até 3 itens {tone: bad|warn|ok|info, icon, title, sub, action, onAction} */
export function Today({ title = "O que fazer hoje", note, items = [] }) {
  const lista = items.slice(0, 3);
  return (
    <div className="today">
      <div className="today-head"><h2>{title}</h2>{note && <span className="eyebrow">{note}</span>}</div>
      {lista.length === 0 && (
        <div className="todo"><span className="ico ok" aria-hidden="true">✓</span><div><strong>Nada urgente agora</strong><span className="todo-sub">Tudo em dia.</span></div></div>
      )}
      {lista.map((it, i) => (
        <div className="todo" key={i}>
          <span className={`ico ${it.tone || "info"}`} aria-hidden="true">{it.icon || "!"}</span>
          <div style={{ minWidth: 0 }}>
            <strong>{it.title}</strong>
            {it.sub && <span className="todo-sub">{it.sub}</span>}
          </div>
          {it.action && <button type="button" className="btn btn-primary btn-sm" onClick={it.onAction}>{it.action}</button>}
        </div>
      ))}
    </div>
  );
}

/** Faixa de KPIs: items = [{label, value, sub, tone, color}] */
export function KpiStrip({ items = [], style = {} }) {
  const n = Math.max(1, Math.min(items.length, 5));
  return (
    <div className="kpi-strip" style={{ "--kpi-n": n, ...style }}>
      {items.map((k, i) => (
        <div className="kpi-cell" key={i}>
          <span className="k">{k.label}</span>
          <span className="v" style={k.color ? { color: k.color } : undefined}>{k.value}</span>
          {k.sub && <span className={`d ${k.tone || ""}`}>{k.sub}</span>}
        </div>
      ))}
    </div>
  );
}

/** Selo de situação: tone = ok | warn | bad | info | off | gold */
export function Selo({ tone = "info", children, dot = true }) {
  return <span className={`pill ${tone}`}>{dot && <span className="dot" />}{children}</span>;
}

/** Explicação recolhida: título visível, corpo ao abrir. */
export function Explica({ title, tone, children, open }) {
  return (
    <details className="explain" open={open}>
      <summary>{tone && <span className={`sev ${tone}`} />}<span>{title}</span></summary>
      <div className="explain-body">{children}</div>
    </details>
  );
}

/** Cabeçalho de bloco (título + linha curta + ações à direita). */
export function BlockHead({ title, sub, children }) {
  return (
    <div className="block-head">
      <div><h2>{title}</h2>{sub && <p>{sub}</p>}</div>
      {children && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div>}
    </div>
  );
}

/**
 * Tabela com linha que abre.
 * columns = [{key, label, align, render(row)}]; detail(row) = conteúdo ao abrir; rowKey(row).
 */
export function ExpandTable({ columns, rows, rowKey = (r) => r.id, detail, onOpen, minWidth = 640, empty = "Nada aqui ainda" }) {
  const [aberto, setAberto] = useState(null);
  const toggle = (k) => setAberto((a) => (a === k ? null : k));
  return (
    <div className="table-card">
      <div className="table-scroll">
        <table className="table" style={{ minWidth }}>
          <thead>
            <tr>
              {detail && <th style={{ width: 28 }} aria-label="Abrir" />}
              {columns.map((c) => <th key={c.key} style={{ textAlign: c.align || "left" }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + (detail ? 1 : 0)} style={{ textAlign: "center", color: "var(--faint)", padding: 28 }}>{empty}</td></tr>
            )}
            {rows.map((r) => {
              const k = rowKey(r);
              const on = aberto === k;
              const abrir = () => (detail ? toggle(k) : onOpen && onOpen(r));
              return (
                <Fragment key={k}>
                  <tr
                    className="row"
                    tabIndex={0}
                    aria-expanded={detail ? on : undefined}
                    onClick={abrir}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); abrir(); } }}
                  >
                    {detail && <td style={{ width: 28, paddingRight: 0 }}><span className="chev" aria-hidden="true">›</span></td>}
                    {columns.map((c) => (
                      <td key={c.key} style={{ textAlign: c.align || "left" }}>{c.render ? c.render(r) : r[c.key]}</td>
                    ))}
                  </tr>
                  {detail && on && (
                    <tr className="detail"><td colSpan={columns.length + 1}>{detail(r)}</td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Filtros em pílula (menores que as abas): items = [{id, label, count}] */
export function Chips({ items, active, onChange, label = "Filtros", style = {} }) {
  return (
    <div className="pill-tabs chips" role="tablist" aria-label={label} style={style}>
      {items.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={active === t.id}
          className={`pill-tab${active === t.id ? " on" : ""}`} onClick={() => onChange(t.id)}>
          {t.label}{t.count != null && <span className="n">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** Barra curta de progresso para tabela: done/total ou pct. */
export function MiniBar({ done, total, pct, width = 90 }) {
  const p = pct != null ? pct : total ? Math.round((done / total) * 100) : 0;
  const tone = p >= 100 ? "ok" : p >= 50 ? "" : p > 0 ? "warn" : "";
  return (
    <span className="minibar" style={{ "--w": `${width}px` }}>
      <span className={`minibar-track ${tone}`}><i style={{ width: `${Math.min(100, p)}%` }} /></span>
      <span className="minibar-n">{total != null ? `${done}/${total}` : `${p}%`}</span>
    </span>
  );
}

/** Caixa de marcar do kit (botão acessível). */
export function Check({ on, onClick, label }) {
  return (
    <button type="button" className={`kcheck${on ? " on" : ""}`} aria-pressed={!!on} aria-label={label}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}>
      {on && "✓"}
    </button>
  );
}

/** Bloco de texto rotulado (usado no conteúdo que abre). */
export function Detalhe({ label, children, pre }) {
  if (!children) return null;
  return (
    <div className="detalhe">
      <span className="eyebrow">{label}</span>
      <div style={pre ? { whiteSpace: "pre-wrap" } : undefined}>{children}</div>
    </div>
  );
}
